import { useDraggable } from '@dnd-kit/core'
import clsx from 'clsx'
import { type ComponentPropsWithoutRef, type FC } from 'react'
import { FaGripVertical } from 'react-icons/fa'

import { LABELS } from '@/constants/common/message.constants'

import { type HeaderCellResizeProps } from './apply-column-layout'
import { ResizableHeaderCell } from './ResizableHeaderCell'

type Props = ComponentPropsWithoutRef<'th'> &
  Partial<HeaderCellResizeProps> & {
    'data-column-key'?: string
    'data-draggable'?: string
  }

/**
 * レイアウト編集モードで、つまみをつかんで列を入れ替え・固定に出し入れできる見出しセル
 * （#3690 VOC_PJ-577、確定版 §2.3・Q-1 の 1-B）。
 *
 * - ドラッグは見出しの左のつまみからだけ始める（見出し全体ではない）。つまみは編集モードの間だけ、
 *   動かせる列にだけ出す（固定必須列には出さない）
 * - 列幅のつまみ（ResizableHeaderCell の右端）はそのまま使える
 * - キーボード・タッチでの並べ替えは持たない（確定版 §2.5）
 */
export const DraggableHeaderCell: FC<Props> = (props) => {
  const columnKey = props['data-column-key']
  const isDraggable = props['data-draggable'] === 'true'
  const { setNodeRef, setActivatorNodeRef, listeners, isDragging } =
    useDraggable({
      id: columnKey ?? '__not-draggable',
      disabled: !columnKey || !isDraggable
    })
  if (!columnKey || !isDraggable) return <ResizableHeaderCell {...props} />
  return (
    <ResizableHeaderCell
      ref={setNodeRef}
      {...props}
      className={clsx(props.className, '!pl-7', isDragging && 'opacity-40')}
    >
      <span
        ref={setActivatorNodeRef}
        role="button"
        aria-label={LABELS.REORDER_COLUMN}
        data-testid={`header-drag-handle-${columnKey}`}
        className={clsx(
          'absolute left-1 top-1/2 flex -translate-y-1/2 p-1 text-gray-400',
          isDragging ? 'cursor-grabbing' : 'cursor-grab'
        )}
        onClick={(event) => event.stopPropagation()}
        {...listeners}
      >
        <FaGripVertical />
      </span>
      {props.children}
    </ResizableHeaderCell>
  )
}
